export const default_web_analytics_settings = {
    enabled: true,
    posthog_listen: false,
    posthog_publish: false,
    ping_interval_ms: 15000,
    flush_interval_ms: 5000,
    batch_size: 8,
    max_pre_init_queue_size: 30,
    max_persisted_events: 30,
    max_persisted_bytes: 32 * 1024,
    max_beacon_payload_bytes: 60 * 1024,
    event_name_rate_limit_per_minute: 120,
    spa_pageview: true,
    auto_pageview: true,
    auto_click: true,
    auto_scroll: true,
    auto_ping: true,
    auto_page_leave: true,
    auto_video: true,
    auto_performance: true,
    capture_early_auto_events: true,
    scroll_thresholds: [25, 50, 75, 100],
    video_progress_thresholds: [25, 50, 75, 100],
    video_timeupdate_throttle_ms: 1000,
    idle_after_ms: 15000
};

function coerce_boolean_setting(value, fallback_value) {
    if (typeof value === 'boolean') {
        return value;
    }
    if (typeof value === 'string') {
        const normalized_value = value.trim().toLowerCase();
        if (['true', '1', 'yes', 'on'].includes(normalized_value)) {
            return true;
        }
        if (['false', '0', 'no', 'off'].includes(normalized_value)) {
            return false;
        }
    }
    return fallback_value;
}

function coerce_positive_number_setting(value, fallback_value) {
    if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
        return value;
    }
    if (typeof value === 'string' && value.trim() !== '') {
        const parsed_value = Number(value);
        if (Number.isFinite(parsed_value) && parsed_value > 0) {
            return parsed_value;
        }
    }
    return fallback_value;
}

function coerce_threshold_array(value, fallback_value) {
    const source_values = Array.isArray(value) ? value : fallback_value;
    const normalized_values = source_values
        .map((threshold_value) => Number(threshold_value))
        .filter((threshold_value) => Number.isFinite(threshold_value) && threshold_value > 0 && threshold_value <= 100)
        .map((threshold_value) => Math.round(threshold_value));
    return Array.from(new Set(normalized_values)).sort((first_value, second_value) => first_value - second_value);
}

/**
 * Builds the final web analytics settings object from defaults, instance-level settings,
 * and constructor config. This is intentionally defensive because settings can come from
 * JavaScript init code, data attributes, organization metadata, or later experiments.
 *
 * @param {Object} config - DataHub constructor config. May contain a web_settings object.
 * @param {Object} existing_settings - Existing settings already attached to the instance.
 * @returns {Object} Normalized web analytics settings.
 */
export function build_web_analytics_settings(config = {}, existing_settings = {}) {
    const configured_settings = config && typeof config === 'object' && config.web_settings && typeof config.web_settings === 'object'
        ? config.web_settings
        : {};
    const merged_settings = {
        ...default_web_analytics_settings,
        ...(existing_settings && typeof existing_settings === 'object' ? existing_settings : {}),
        ...configured_settings
    };

    return {
        ...merged_settings,
        enabled: coerce_boolean_setting(merged_settings.enabled, default_web_analytics_settings.enabled),
        posthog_listen: coerce_boolean_setting(merged_settings.posthog_listen, default_web_analytics_settings.posthog_listen),
        posthog_publish: coerce_boolean_setting(merged_settings.posthog_publish, default_web_analytics_settings.posthog_publish),
        spa_pageview: coerce_boolean_setting(merged_settings.spa_pageview, default_web_analytics_settings.spa_pageview),
        auto_pageview: coerce_boolean_setting(merged_settings.auto_pageview, default_web_analytics_settings.auto_pageview),
        auto_click: coerce_boolean_setting(merged_settings.auto_click, default_web_analytics_settings.auto_click),
        auto_scroll: coerce_boolean_setting(merged_settings.auto_scroll, default_web_analytics_settings.auto_scroll),
        auto_ping: coerce_boolean_setting(merged_settings.auto_ping, default_web_analytics_settings.auto_ping),
        auto_page_leave: coerce_boolean_setting(merged_settings.auto_page_leave, default_web_analytics_settings.auto_page_leave),
        auto_video: coerce_boolean_setting(merged_settings.auto_video, default_web_analytics_settings.auto_video),
        auto_performance: coerce_boolean_setting(merged_settings.auto_performance, default_web_analytics_settings.auto_performance),
        capture_early_auto_events: coerce_boolean_setting(merged_settings.capture_early_auto_events, default_web_analytics_settings.capture_early_auto_events),
        ping_interval_ms: coerce_positive_number_setting(merged_settings.ping_interval_ms, default_web_analytics_settings.ping_interval_ms),
        flush_interval_ms: coerce_positive_number_setting(merged_settings.flush_interval_ms, default_web_analytics_settings.flush_interval_ms),
        batch_size: coerce_positive_number_setting(merged_settings.batch_size, default_web_analytics_settings.batch_size),
        max_pre_init_queue_size: coerce_positive_number_setting(merged_settings.max_pre_init_queue_size, default_web_analytics_settings.max_pre_init_queue_size),
        max_persisted_events: coerce_positive_number_setting(merged_settings.max_persisted_events, default_web_analytics_settings.max_persisted_events),
        max_persisted_bytes: coerce_positive_number_setting(merged_settings.max_persisted_bytes, default_web_analytics_settings.max_persisted_bytes),
        max_beacon_payload_bytes: coerce_positive_number_setting(merged_settings.max_beacon_payload_bytes, default_web_analytics_settings.max_beacon_payload_bytes),
        event_name_rate_limit_per_minute: coerce_positive_number_setting(merged_settings.event_name_rate_limit_per_minute, default_web_analytics_settings.event_name_rate_limit_per_minute),
        video_timeupdate_throttle_ms: coerce_positive_number_setting(merged_settings.video_timeupdate_throttle_ms, default_web_analytics_settings.video_timeupdate_throttle_ms),
        idle_after_ms: coerce_positive_number_setting(merged_settings.idle_after_ms, default_web_analytics_settings.idle_after_ms),
        scroll_thresholds: coerce_threshold_array(merged_settings.scroll_thresholds, default_web_analytics_settings.scroll_thresholds),
        video_progress_thresholds: coerce_threshold_array(merged_settings.video_progress_thresholds, default_web_analytics_settings.video_progress_thresholds)
    };
}